import { useEffect, useMemo, useRef } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type PromptsRealtimeParams = {
  brandId?: string;
  countries?: string[];
  providers?: string[];
  days?: number;
  startDate?: string | null;
  endDate?: string | null;
  enabled?: boolean;
  queryKey: QueryKey;
};

type PromptsFilters = {
  brandId: string;
  countries?: string[] | null;
  providers?: string | null;
  days?: number | null;
  startDate?: string | null;
  endDate?: string | null;
};

type PromptsSocketPayload = {
  brandId: string;
  filters?: PromptsFilters;
  clusters: any[];
};

const normalizeCountries = (countries?: string[]) => {
  if (!countries || !countries.length) return null;
  const normalized = Array.from(
    new Set(
      countries.map((country) => country.trim().toUpperCase()).filter(Boolean),
    ),
  ).sort();
  return normalized.length ? normalized : null;
};

const buildSignature = (filters: PromptsFilters) =>
  `${filters.brandId}:${(filters.countries ?? []).join(",")}:${filters.providers ?? ""}:${filters.days ?? ""}:${filters.startDate ?? ""}:${filters.endDate ?? ""}`;

const normalizeProviders = (providers?: string[]) => {
  if (!providers?.length) return null;
  const normalized = Array.from(
    new Set(
      providers
        .map((provider) => String(provider || "").trim().toUpperCase())
        .filter(Boolean),
    ),
  ).sort();
  return normalized.length ? normalized.join(",") : null;
};

const ALLOWED_DAYS = new Set([1, 7, 14, 30, 90]);

const normalizeDays = (days?: number) => {
  const parsed = Number.parseInt(String(days ?? ""), 10);
  if (ALLOWED_DAYS.has(parsed)) return parsed;
  return null;
};

export const usePromptsRealtime = ({
  brandId,
  countries,
  providers,
  days,
  startDate,
  endDate,
  enabled = true,
  queryKey,
}: PromptsRealtimeParams) => {
  const queryClient = useQueryClient();
  const latestQueryKeyRef = useRef<QueryKey>(queryKey);
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addPromptsSubscription = useSocketStore(
    (state) => state.addPromptsSubscription,
  );
  const removePromptsSubscription = useSocketStore(
    (state) => state.removePromptsSubscription,
  );

  useEffect(() => {
    latestQueryKeyRef.current = queryKey;
  }, [queryKey]);

  const filters = useMemo<PromptsFilters | null>(() => {
    if (!brandId) return null;
    return {
      brandId,
      countries: normalizeCountries(countries),
      providers: normalizeProviders(providers),
      days: normalizeDays(days),
      startDate: startDate || null,
      endDate: endDate || null,
    };
  }, [brandId, countries, providers, days, startDate, endDate]);

  useEffect(() => {
    if (!enabled || !filters) {
      return;
    }
    const signature = buildSignature(filters);
    const socket = ensureSocket(API_BASE_URL);

    const subscribePayload = {
      brandId: filters.brandId,
      countries: filters.countries ?? undefined,
      providers: filters.providers ?? undefined,
      days: filters.days ?? undefined,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
    };

    const handleResults = (payload: PromptsSocketPayload) => {
      if (!payload?.filters) return;
      const payloadSignature = buildSignature(payload.filters);
      if (payloadSignature !== signature) return;
      if (!Array.isArray(payload.clusters)) {
        void queryClient.invalidateQueries({
          queryKey: latestQueryKeyRef.current,
          refetchType: "active",
        });
        return;
      }
      queryClient.setQueryData(
        latestQueryKeyRef.current,
        payload.clusters,
      );
    };

    socket.on("prompts:results", handleResults);
    addPromptsSubscription(signature, subscribePayload);

    return () => {
      socket.off("prompts:results", handleResults);
      removePromptsSubscription(signature);
    };
  }, [
    enabled,
    filters,
    queryClient,
    ensureSocket,
    addPromptsSubscription,
    removePromptsSubscription,
  ]);
};
